/**
 * CTIDashboard — Main dashboard with MetricCards showing document stats.
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MetricCard } from '../../../components/MetricCard';
import { DataTable, type Column } from '../../../components/DataTable';
import { StatusBadge } from '../../../components/StatusBadge';
import { LoadingState } from '../../../components/LoadingState';
import { ErrorState } from '../../../components/ErrorState';
import { useAuth } from '../../../components/AuthProvider';
import { listDocuments } from '../../../api/documents';
import type { DocumentMetadata } from '../../../api/types';

export function CTIDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [documents, setDocuments] = useState<DocumentMetadata[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchDocuments = async () => {
    if (!user) return;
    setLoading(true);
    setError(null);
    try {
      const response = await listDocuments(user.tenantId);
      setDocuments(response.documents);
    } catch {
      setError('Failed to load documents. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  if (loading) return <LoadingState message="Loading dashboard..." />;
  if (error) return <ErrorState message={error} onRetry={fetchDocuments} />;

  // Compute metrics
  const totalDocs = documents.length;
  const completedDocs = documents.filter((d) => d.status === 'completed' || d.status === 'indexed').length;
  const processingDocs = documents.filter((d) =>
    ['sanitizing', 'extracting', 'mapping', 'scoring', 'indexing'].includes(d.status)
  ).length;
  const failedDocs = documents.filter((d) => d.status.includes('failed')).length;

  // Recent documents
  const recentDocs = [...documents]
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
    .slice(0, 5);

  const columns: Column<DocumentMetadata>[] = [
    { key: 'file_name', header: 'File Name' },
    { key: 'file_type', header: 'Type', width: '80px' },
    {
      key: 'status',
      header: 'Status',
      render: (row) => <StatusBadge status={row.status} />,
    },
    {
      key: 'created_at',
      header: 'Uploaded',
      render: (row) => new Date(row.created_at).toLocaleDateString(),
    },
  ];

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-heading-lg font-bold text-slate-text">CTI Dashboard</h1>
        <p className="text-body text-slate-muted mt-1">
          Overview of your threat intelligence pipeline
        </p>
      </div>

      {/* Metric Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <MetricCard title="Total Documents" value={totalDocs} subtitle="All uploaded CTI files" />
        <MetricCard title="Completed" value={completedDocs} subtitle="Fully processed" trend="up" />
        <MetricCard title="Processing" value={processingDocs} subtitle="In pipeline" trend="neutral" />
        <MetricCard title="Failed" value={failedDocs} subtitle="Needs attention" trend={failedDocs > 0 ? 'down' : 'neutral'} />
      </div>

      {/* Recent Documents */}
      <div>
        <h2 className="text-section font-bold text-slate-text mb-4">Recent Uploads</h2>
        <DataTable
          columns={columns}
          data={recentDocs}
          keyExtractor={(row) => row.document_id}
          emptyMessage="No documents uploaded yet. Start by uploading a CTI file."
          onRowClick={(row) => navigate(`/workflow?doc=${row.document_id}`)}
        />
      </div>
    </div>
  );
}
